import { useState, useEffect } from 'react';
import { api } from '@/utils/api';
import { Loader2, Plus, Trash2 } from 'lucide-react';

interface PositionRecord {
  id: string;
  name: string;
  departmentId: string | null;
  isActive: boolean;
}

interface PositionDeptRecord {
  id: string;
  name: string;
  disabled?: boolean;
}

// A position with no department is available to every department. HR calls
// these "global" positions; the UI labels them "Any department".
const GLOBAL_GROUP = '__global__';

export function PositionsSection() {
  const [positions, setPositions] = useState<PositionRecord[]>([]);
  const [departments, setDepartments] = useState<PositionDeptRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  // Keyed by position id: the server's own words stay pinned to the row they
  // describe. A 409 names how many employees hold the position.
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [newName, setNewName] = useState('');
  const [newDeptId, setNewDeptId] = useState('');
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);

  const loadData = async () => {
    try {
      const [posRes, deptRes] = await Promise.all([
        // 🔒 NO activeOnly here, deliberately. Settings is the ONLY surface that
        // can switch a disabled position back on, so it must be able to see the
        // disabled ones. The employee pickers are the ones that send
        // ?activeOnly=true and therefore never offer a switched-off position.
        api.get('/hr-positions'),
        api.get('/departments'),
      ]);
      setPositions(posRes.data?.data ?? []);
      setDepartments(deptRes.data?.data ?? []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const deptName = (id: string | null) =>
    (id && departments.find(d => d.id === id)?.name) || 'Any department';

  const toggleActive = async (pos: PositionRecord, isActive: boolean) => {
    setBusy(pos.id);
    setRowError(prev => ({ ...prev, [pos.id]: '' }));
    try {
      await api.patch(`/hr-positions/${pos.id}`, { isActive });
      // Refetch rather than patching state in place — the server decides what a
      // position looks like after a write, not this component.
      await loadData();
    } catch (err: any) {
      setRowError(prev => ({ ...prev, [pos.id]: err?.response?.data?.error || 'Failed to update position' }));
    } finally { setBusy(null); }
  };

  const deletePosition = async (pos: PositionRecord) => {
    setBusy(pos.id);
    setRowError(prev => ({ ...prev, [pos.id]: '' }));
    try {
      await api.delete(`/hr-positions/${pos.id}`);
      await loadData();
    } catch (err: any) {
      // 🔒 Surface the server's message VERBATIM. On a 409 it names how many
      // employees still hold the position and tells HR to disable it instead —
      // a generic "Failed to delete" throws away the only actionable part.
      setRowError(prev => ({ ...prev, [pos.id]: err?.response?.data?.error || 'Failed to delete position' }));
    } finally { setBusy(null); }
  };

  const createPosition = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setCreateError('');
    try {
      await api.post('/hr-positions', { name, departmentId: newDeptId || null });
      setNewName('');
      setNewDeptId('');
      await loadData();
    } catch (err: any) {
      setCreateError(err?.response?.data?.error || 'Failed to create position');
    } finally { setCreating(false); }
  };

  if (loading) {
    return (
      <div className="mt-8 flex items-center gap-2 border-t border-gray-200 pt-6 text-sm text-gray-400">
        <Loader2 size={16} className="animate-spin" /> Loading positions...
      </div>
    );
  }

  // Globals first — they apply everywhere, so they are the ones HR scans for
  // before adding a department-specific duplicate.
  const groupIds = [
    GLOBAL_GROUP,
    ...departments.filter(d => positions.some(p => p.departmentId === d.id)).map(d => d.id),
  ];

  return (
    <div className="mt-8 border-t border-gray-200 pt-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Positions</h2>
        <p className="text-sm text-gray-500">
          Job titles employees can hold. Switch a position off to retire it without losing the
          employees who hold it — disabled positions stop appearing when onboarding someone.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
        <input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          placeholder="New position name"
          className="min-w-[200px] flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
        />
        <select
          value={newDeptId}
          onChange={e => setNewDeptId(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
        >
          <option value="">Any department</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <button
          type="button"
          onClick={createPosition}
          disabled={creating || !newName.trim()}
          className="flex items-center gap-1 rounded-lg bg-[var(--ink-0)] px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add position
        </button>
      </div>
      {createError && <p className="mb-3 text-sm text-red-700">{createError}</p>}

      <div className="space-y-4">
        {groupIds.map(groupId => {
          const rows = positions.filter(p =>
            groupId === GLOBAL_GROUP ? !p.departmentId : p.departmentId === groupId);
          if (rows.length === 0) return null;
          return (
            <div key={groupId}>
              <h3 className="mb-1 text-xs font-medium uppercase text-gray-500">
                {groupId === GLOBAL_GROUP ? 'Any department' : deptName(groupId)}
              </h3>
              <div className="rounded-lg border border-gray-200 bg-white">
                {rows.map(pos => (
                  <div key={pos.id} className="border-b border-gray-100 px-4 py-2 last:border-b-0">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <span className={`text-sm ${pos.isActive ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                          {pos.name}
                        </span>
                        <p className="text-xs text-gray-500">{deptName(pos.departmentId)}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={pos.isActive}
                            disabled={busy === pos.id}
                            onChange={e => void toggleActive(pos, e.target.checked)}
                          />
                          <span className="text-xs text-gray-600">{pos.isActive ? 'On' : 'Off'}</span>
                        </label>
                        <button
                          type="button"
                          title="Delete position"
                          disabled={busy === pos.id}
                          onClick={() => void deletePosition(pos)}
                          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    {rowError[pos.id] && (
                      <p className="mt-1 text-xs text-red-700">{rowError[pos.id]}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {positions.length === 0 && <p className="text-sm text-gray-400">No positions yet.</p>}
      </div>
    </div>
  );
}
